<template>
  <div class="app-main-content">
    <!-- 需要在切换回来后，页面保持不变，所以使用v-show -->
    <order-tree-grid
      v-show="view==='list'"
      ref="orderTreeGrid"
      :context="context"
      :use-order-center-mode="useOrderCenterMode"
      :order-center-branch="useOrderCenterBranch"
      :approve-cooperation-business="approveCooperationBusiness"
      @look="lookHandle"
      @add="addHandle"
      @edit="editHandle"
      @copy="copyHandle"
    />
    <!-- 因为占用内存多，所以使用v-if，每次需要使用时都重新创建 -->
    <order-workspace
      v-if="view==='detail'"
      ref="orderWorkspace"
      :parent-context="context"
      :use-order-center-mode="useOrderCenterMode"
      :approve-cooperation-business="approveCooperationBusiness"
      @add="addHandle"
      @edit="editHandle"
      @copy="copyHandle"
      @back="backToList"
    />
    <!-- 操作模式弹窗 -->
    <air-op-type-dialog
      :visible.sync="airOpTypeDialog.visible"
      @successHandle="airOpTypeSuccessHandle"
      @cancel="airOpTypeCancelHandle"
    />
    <!--业务复制选择复制哪些信息-->
    <job-copy-window
      :visible.sync="copyDialog.visible"
      :lob="copyDialog.job.lob || ''"
      @confirmCallBack="confirmCallBackHandler"
    />
    <job-no-rule-selector
      :visible.sync="jobNoRule.visible"
      :rules="jobNoRule.rules || []"
      @selectedBack="doCopyJob"
    />
  </div>
</template>

<script>
import { AiOrderContext } from './aiOrderContext'
import airOpTypeDialog from './components/booking/airOPTypeDialog.vue'
import orderTreeGrid from './components/order/orderTreeGrid.vue'
import orderWorkspace from './components/order/orderWorkspace.vue'
import jobCopyWindow from '@/components/Cargo/Job/jobCopyWindow'
import jobNoRuleSelector from '@/components/Cargo/Job/jobNoRuleSelector'
import { ErrorTip } from '@/utils/class/ErrorTip'
import LocalStorageUtils from '@/utils/localStorageUtils'
import * as SystemConstants from '@/class/cargo/model/parameters/SystemConstants'
import { SecurityUtil } from '@/class/cargo/model/utils/SecurityUtil'
import { AirImportJob } from '@/class/model/booking/AirImportJob'
import { getUserDefaultDepartment } from '@/api/user'
import { isContractExpiredForbidCreateJob, queryJobNoRule, doCopyJob, advancedQuery } from '@/api/AirImport/aiBooking'
import { getLoading } from '@/utils/utilBaseElement'
import { JobUtils } from '@/class/cargo/model/utils/JobUtils'
import { cacheName, cache } from '@/utils/cacheManager'
import AdvancedSearchOption from '@/class/cargo/model/parameters/AdvancedSearchOption'
import { EntityDefaultValueUtil } from '@/class/cargo/model/utils/EntityDefaultValueUtil'
import { JumpUtil } from '@/utils/JumpUtil'

export default {
  name: 'AiOrder',
  components: { airOpTypeDialog, orderTreeGrid, orderWorkspace, jobCopyWindow, jobNoRuleSelector },
  data() {
    return {
      loading: null,
      context: new AiOrderContext(),
      view: 'list',
      airOpTypeDialog: {
        visible: false,
        promise: null,
        resolve: null,
        reject: null
      },
      copyDialog: {
        visible: false,
        job: {}
      },
      jobNoRule: {
        visible: false,
        rules: ''
      }
    }
  },
  computed: {
    useOrderCenterMode() { // 是否开启接单中心模式
      return cache.config ? Number(cache.config.get('useOrderCenterMode')) === 1 : false
    },
    useOrderCenterBranch() { // 是否开启接单分公司
      return cache.config ? Number(cache.config.get('orderCenterBranch')) === 1 : false
    },
    approveCooperationBusiness() { // 是否协作分公司需审核接单分公司的协作业务
      return cache.config ? Number(cache.config.get('approveCooperationBusiness')) === 1 : false
    }
  },
  errorCaptured(err) {
    if (err instanceof ErrorTip) {
      this.$message({ message: err.message, type: err.type })
      return false
    }
    return true
  },
  async created() {
    await cache.load(cacheName.CONFIG)
    this._defalutOpDepartmentMap = new Map()
  },
  activated() {
    if (this.$route.query.jobNo) {
      this.toDetail(this.$route.query.jobNo)
      location.replace(location.href.replace(location.hash, `#${this.$route.path}`))
    } else if (this.$route.params.jobNos) {
      const filter = {
        conditions: [
          new AdvancedSearchOption(null, 'job.OP_OFFICE', this.$store.getters.currentOfficeId).formatObj2Json(),
          new AdvancedSearchOption(null, 'job.JOB_STATE', SystemConstants.STATE_VERIFYING.code).formatObj2Json()
        ]
      }
      this.view = 'detail'
      this.$nextTick(() => {
        JumpUtil.jumpToTab(advancedQuery, filter, this.$route.params, this, 'orderWorkspace')
      })
    }
  },
  methods: {
    lookHandle(row) {
      this.detailHandle('look', row)
    },
    async addHandle() {
      if (JobUtils.checkHQ()) { return }
      const row = new AirImportJob().fillDefaultValue().getData()
      try {
        row.operateType = await this.confirmOpType()
      } catch (err) {
        return
      }
      const results = await Promise.all([
        this.getDefaultJob(),
        this.getOpDepartment()
      ])
      const defaultJob = results[0]
      const opDepartment = results[1]
      row.lob = 'AI'
      row.cargoSource = 'SC'
      row.cargoType = 'GC'
      row.paymentTermHBL = 'PPBYPP'
      row.paymentTermMBL = 'CCBYPP'
      row.incoTerm = '内贸'
      row.tradeTerm = '110'
      row.taxNature = 0
      row.opOffice = this.$store.getters.currentOfficeId
      if (opDepartment && Number(opDepartment.departmentId) > 0) {
        row.op = this.context.user.userId
        if (this.context.user.defaultDepId > 0 && this.context.user.defaultOfficeId === this.$store.getters.currentOfficeId) {
          row.opDepartment = this.context.user.defaultDepId
        } else {
          row.opDepartment = opDepartment.departmentId
        }
      }
      Object.assign(row, defaultJob)
      this.detailHandle('add', row)
    },
    async confirmOpType() {
      const airOpTypeDialog = this.airOpTypeDialog
      airOpTypeDialog.promise = new Promise((resolve, reject) => {
        airOpTypeDialog.resolve = resolve
        airOpTypeDialog.reject = reject
      })
      airOpTypeDialog.visible = true
      return airOpTypeDialog.promise
    },
    airOpTypeSuccessHandle(operateType) {
      this.airOpTypeDialog.resolve(operateType)
    },
    airOpTypeCancelHandle() {
      this.airOpTypeDialog.reject('cancel')
    },
    async getDefaultJob() {
      const entityDefValUtil = EntityDefaultValueUtil.getInstance()
      if (!entityDefValUtil.defaultValues) {
        await entityDefValUtil.initEntityDefaultValue()
      }
      return entityDefValUtil.airImportJob.getData()
    },
    async getOpDepartment() {
      const officeId = this.$store.getters.currentOfficeId
      let opDepartment = this._defalutOpDepartmentMap.get(officeId)
      if (!opDepartment) {
        opDepartment = await getUserDefaultDepartment({ category: 'role', shape: 'op', userId: 0, officeId }).then(res => res.data)
        this._defalutOpDepartmentMap.set(officeId, opDepartment)
      }
      return opDepartment
    },
    async editHandle(row) {
      // 协作状态
      if (row && row.cooperateStatus !== -1 && this.useOrderCenterMode && this.approveCooperationBusiness) {
        if (row.cooperateStatus !== SystemConstants.COOPERATE_STATUS_APPROVED.code) {
          const cooperateStatusStr = JobUtils.getStateName(row.cooperateStatus, SystemConstants.COOPERATE_STATUS)
          this.$message.warning(this.$t('Message.CanNotEditJob', [this.$t('CargoMsg.Cargo_CooperateStatus') + cooperateStatusStr]))
          return
        }
        this.detailHandle('edit', row)
        return
      }
      if (row.jobState > -1) {
        this.$message.info(this.$t('queryJob.PromissoryHaveBecomeOfficialBusiness'))
        return
      }
      if (!await SecurityUtil.canEdit(new AirImportJob(row))) {
        this.$message.info(this.$t('Message.NoPermissionToEditTheJob'))
        return
      }
      this.detailHandle('edit', row)
    },
    copyHandle(row) {
      if (JobUtils.checkHQ()) { return }
      if (row && row.customerId) {
        this.loading = getLoading()
        isContractExpiredForbidCreateJob(row.customerId).then(res => {
          this.loading.close()
          if (res.data) {
            this.$message.info(this.$t('Message.ContractExpireCannotCreated'))
          } else {
            this.copyDialog.job = row
            const isShowView = LocalStorageUtils.getConfiguration('COPY_JOB_' + row.lob) // 是否显示复制选项弹框【勾选是true不显示】
            if (!isShowView) {
              this.copyDialog.visible = true
            } else {
              this.confirmCallBackHandler()
            }
          }
        })
      }
    },
    async confirmCallBackHandler() {
      await this.context.prepareData()
      if (this.context.config.get('UseNewJobNoRule') === '1') {
        this.loading = getLoading()
        queryJobNoRule(SystemConstants.RE.code).then(res => {
          this.ruleResultHandler(res)
        }).finally(() => {
          this.loading.close()
        })
      } else {
        this.doCopyJob()
      }
    },
    ruleResultHandler(event) {
      const rules = event.data
      if (rules && rules.length > 1) {
        this.jobNoRule.visible = true
        this.jobNoRule.rules = rules
      } else if (rules && rules.length > 0) {
        const rule = rules[0]
        this.doCopyJob(rule)
      } else {
        this.$message.warning(this.$t('SeaExport.MaintainJobNoRule'))
        return
      }
    },
    doCopyJob(jobNoRule) {
      const item = {}
      item.jobRule = !jobNoRule ? '' : jobNoRule.uuid
      item.jobUuid = this.copyDialog.job.uuid
      item.branchId = this.$store.getters.currentOfficeId
      /* 复制哪些页签的数据 */
      const lob = this.copyDialog.job.lob
      item.charge = LocalStorageUtils.getConfiguration('COPY_CHARGE_' + lob)
      item.all = false
      this.loading = getLoading()
      doCopyJob(item).then(async res => {
        const sourceJob = this.copyDialog.job
        const newJob = res.data
        newJob.loadingAgentId = sourceJob.loadingAgentId
        newJob.voyageFlight = ''
        newJob.dischargingPortCode = ''
        newJob.quantityContract = 0
        newJob.packing = ''
        newJob.weightContract = 0
        newJob.cubageContract = 0
        newJob.chargeWeight = 0
        newJob.cargoSalesId = 0
        newJob.groundAgentId = 0
        if (newJob.sales > 0) {
          newJob.salesOffice = sourceJob.salesOffice
          newJob.salesDepartment = sourceJob.salesDepartment
        }
        newJob.op = sourceJob.op
        const continueExec = await JobUtils.validateJobFieldActive(newJob, this, 'AiOrder')
        if (!continueExec) {
          return
        }
        this.detailHandle('copy', newJob)
      }).finally(() => {
        this.loading.close()
      })
    },
    // 进详情处理
    detailHandle(type, row) {
      let rowData = null
      if (this.view === 'detail') {
        rowData = this.$refs.orderWorkspace.rowData
      } else {
        this.view = 'detail'
        rowData = this.$refs.orderTreeGrid.rowData
      }
      if (this.$refs.orderWorkspace) {
        this.$refs.orderWorkspace.onShow(type, rowData, row)
      } else {
        this.$nextTick(() => {
          this.$refs.orderWorkspace.onShow(type, rowData, row)
        })
      }
    },
    backToList(isRefresh) {
      this.view = 'list'
      if (isRefresh) {
        const orderTreeGridRef = this.$refs.orderTreeGrid
        if (orderTreeGridRef.filter.conditions.length > 0) {
          orderTreeGridRef.toQuery()
        } else {
          orderTreeGridRef.rowData = this.$refs.orderWorkspace.rowData
        }
      }
    },
    // 查询job后跳转详情
    toDetail(jobNo) {
      const params = {
        conditions: [new AdvancedSearchOption(null, 'job.JOB_NO', jobNo).formatObj2Json()],
        branchId: this.$store.getters.currentOfficeId,
        pageNum: 1
      }
      this.view = 'detail'
      advancedQuery(params).then(res => {
        const rowData = res.data.list
        if (rowData && rowData.length !== 0) {
          const index = rowData.findIndex(p => p.jobNo === jobNo)
          if (index > 0) {
            this.$refs.orderWorkspace.onShow('look', rowData, rowData[index])
          } else {
            this.$refs.orderWorkspace.onShow('look', rowData, rowData[0])
          }
        } else {
          this.$message.error(this.$t('Message.NoRecordMeetTheConditions'))
          this.$refs.orderWorkspace.goBack()
        }
      }).catch(() => {
        this.$message.error(this.$t('Message.JumpFailed'))
      })
    }
  }
}
</script>
